import { useCallback, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { Paths, buildPath } from "@/common/paths/paths";
import { useMyHardware } from "@/common/hooks/use-my-hardware.hook";
import { useShipmentOptions } from "@/common/hooks/use-shipment-options.hook";
import { findMyHardwareDevice } from "@/pages/Replacement/hooks/use-replaced-device.hook";
import { useDeviceOptions } from "@/pages/Replacement/steps/ChooseDevice/hooks/use-device-options.hook";
import { useReplacementDraftStore } from "@/pages/Replacement/use-replacement-draft-store";
import { createReplacementOrderService } from "@/services/replacement-order/replacement-order-service.factory";
import { toReplacementOrderRequest } from "@/services/replacement-order/replacement-order.mapper";
import type { ReplacementOrderResult } from "@/services/replacement-order/replacement-order.types";
import { REPLACEMENT_ORDER_ERROR_CODE } from "@/services/replacement-order/replacement-order.constants";
import { ReplacementOrderSubmitError } from "@/services/replacement-order/replacement-order-submit-error";
import type { SubmitFailure } from "../review.types";

const replacementOrderService = createReplacementOrderService();

export interface UsePlaceReplacementOrderParams {
  assetId: string;
  category: string;
  onOrderPlaced: (result: ReplacementOrderResult) => void;
}

export interface PlaceReplacementOrder {
  placeOrder: () => void;
  isPending: boolean;
  failure?: SubmitFailure;
  reset: () => void;
}

const toSubmitFailure = (error: Error, serialNumber?: string): SubmitFailure => {
  if (!(error instanceof ReplacementOrderSubmitError)) {
    return { code: REPLACEMENT_ORDER_ERROR_CODE.unknown };
  }
  if (error.code === REPLACEMENT_ORDER_ERROR_CODE.activeOrderExists && serialNumber) {
    return {
      code: error.code,
      existingOrderPath: buildPath(Paths.lcrOrderTracking.url, { serialNumber }),
    };
  }
  return { code: error.code };
};

export const usePlaceReplacementOrder = ({
  assetId,
  category,
  onOrderPlaced,
}: UsePlaceReplacementOrderParams): PlaceReplacementOrder => {
  const draft = useReplacementDraftStore();
  const { data: myHardware } = useMyHardware();
  const replacedAsset = findMyHardwareDevice(myHardware?.devices ?? [], assetId);
  const { options } = useDeviceOptions({ assetId, category });
  const chosenOption = options.find((option) => option.id === draft.deviceId);
  const { deliveryOptions } = useShipmentOptions({
    make: chosenOption?.make ?? "",
    model: chosenOption?.model ?? "",
  });
  const inFlight = useRef(false);
  const clientRequestId = useRef<string>();

  const mutation = useMutation<ReplacementOrderResult, Error>({
    mutationFn: async () => {
      if (!replacedAsset) {
        throw new Error("Cannot place a replacement order before the replaced laptop is loaded");
      }
      if (!chosenOption) {
        throw new Error("Cannot place a replacement order before the chosen device is loaded");
      }
      const pickupBuilding = deliveryOptions.find(
        (option) =>
          option.deliveryMethod === draft.pickup?.deliveryMethod &&
          option.buildingCode === draft.pickup?.buildingCode
      );
      clientRequestId.current ??= crypto.randomUUID();
      return replacementOrderService.submit(
        toReplacementOrderRequest(draft, {
          clientRequestId: clientRequestId.current,
          serialNumber: replacedAsset.serialNumber,
          replacedDevice: { make: replacedAsset.make, model: replacedAsset.model },
          selectedDevice: {
            make: chosenOption.make,
            model: chosenOption.model,
            specs: chosenOption.specs,
          },
          pickupBuildingAddress: pickupBuilding?.address,
        })
      );
    },
    onSuccess: (result) => onOrderPlaced(result),
    onSettled: () => {
      inFlight.current = false;
    },
  });

  // Guards against a second activation within the same submit synchronously:
  // mutation.isPending only turns true on the next render, so a rapid double
  // activation would otherwise issue two submits before the first re-render.
  const placeOrder = useCallback(() => {
    if (inFlight.current) return;
    inFlight.current = true;
    mutation.mutate();
  }, [mutation]);

  const { reset: resetMutation } = mutation;
  const reset = useCallback(() => {
    clientRequestId.current = undefined;
    resetMutation();
  }, [resetMutation]);

  return {
    placeOrder,
    isPending: mutation.isPending,
    failure: mutation.error ? toSubmitFailure(mutation.error, replacedAsset?.serialNumber) : undefined,
    reset,
  };
};
